import { EventType } from "@/analytics/analytics-events";
import { IconText } from "@/registration-tools/common/icon-text";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  Checkmark2Icon,
  FaroDialog,
  FaroText,
  FaroTooltip,
  HistoryRedoIcon,
  HistoryUndoIcon,
  ToolButton,
  ToolGroup,
  Toolbar,
  TranslateVar,
  grey,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { Box, Popper, PopperProps } from "@mui/material";
import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { clearUserEdits } from "../store/data-preparation-actions";
import {
  selectCanRedo,
  selectCanUndo,
  selectLatestRedoStoredEdit,
  selectLatestUndoStoredEdit,
} from "../store/user-edits/data-preparation-user-edit-selectors";
import {
  redoLastUserEdit,
  undoLastUserEdit,
} from "../store/user-edits/data-preparation-user-edit-slice";
import {
  UserEdit,
  UserEditType,
  isConnectionEdit,
} from "../store/user-edits/user-edit-types";

enum ActionType {
  undo = "undo",
  redo = "redo",
}

/** Time (ms) that the undo/redo message box will be shown for, after which it will stop rendering */
const UNDO_REDO_POPUP_LIFE_TIME = 2000;

/** @returns Toolbar for undo, redo, and reset actions. */
export function UndoRedoToolbar(): JSX.Element {
  const dispatch = useAppDispatch();

  const canUndo = useAppSelector(selectCanUndo);
  const canRedo = useAppSelector(selectCanRedo);

  const lastUndoUserEdit = useAppSelector(selectLatestUndoStoredEdit);
  const lastRedoUserEdit = useAppSelector(selectLatestRedoStoredEdit);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const toolGroupRef = useRef<HTMLDivElement>(null);

  const [isUndoTooltipOpen, setIsUndoTooltipOpen] = useState(false);
  const [isRedoTooltipOpen, setIsRedoTooltipOpen] = useState(false);

  const handleUndo = useCallback(
    (inputSource: "toolbar" | "keyboard") => {
      if (!lastUndoUserEdit) {
        return;
      }

      sendUndoAnalyticsEvent(lastUndoUserEdit, inputSource);
      dispatch(undoLastUserEdit());
      setIsUndoTooltipOpen(true);
    },
    [dispatch, lastUndoUserEdit],
  );

  const handleUndoClose = useCallback(() => {
    setIsUndoTooltipOpen(false);
  }, []);

  const handleRedo = useCallback(
    (inputSource: "toolbar" | "keyboard") => {
      if (!lastRedoUserEdit) {
        return;
      }

      sendRedoAnalyticsEvent(lastRedoUserEdit, inputSource);
      dispatch(redoLastUserEdit());
      setIsRedoTooltipOpen(true);
    },
    [dispatch, lastRedoUserEdit],
  );

  const handleRedoClose = useCallback(() => {
    setIsRedoTooltipOpen(false);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const resetAllEdits = useCallback(() => {
    sendResetAnalyticsEvent();
    dispatch(clearUserEdits());
    closeDialog();
  }, [dispatch, closeDialog]);

  const [undoButtonRef, setUndoButtonRef] = useState<HTMLButtonElement | null>(
    null,
  );
  const [redoButtonRef, setRedoButtonRef] = useState<HTMLButtonElement | null>(
    null,
  );

  // Handle keyboard shortcuts
  useEffect(() => {
    function onKeyboardPress(event: KeyboardEvent): void {
      const isUndo =
        (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z";
      const isRedo =
        (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "y";

      if (isUndo && canUndo) {
        event.preventDefault();
        handleUndo("keyboard");
      } else if (isRedo && canRedo) {
        event.preventDefault();
        handleRedo("keyboard");
      }
    }

    window.addEventListener("keydown", onKeyboardPress);
    return () => {
      window.removeEventListener("keydown", onKeyboardPress);
    };
  }, [canUndo, canRedo, handleUndo, handleRedo]);

  return (
    <>
      <Toolbar>
        <ToolGroup ref={toolGroupRef}>
          <FaroTooltip title="Undo Action" placement="left">
            <ToolButton
              ref={setUndoButtonRef}
              onClick={() => handleUndo("toolbar")}
              disabled={!canUndo}
              selected={false}
              aria-label="Undo Action"
            >
              <HistoryUndoIcon />
            </ToolButton>
          </FaroTooltip>

          <FaroTooltip title="Redo Action" placement="left">
            <ToolButton
              ref={setRedoButtonRef}
              onClick={() => handleRedo("toolbar")}
              disabled={!canRedo}
              selected={false}
              aria-label="Redo Action"
            >
              <HistoryRedoIcon />
            </ToolButton>
          </FaroTooltip>
        </ToolGroup>
      </Toolbar>

      <UndoRedoActionTooltip
        anchorEl={undoButtonRef}
        type={ActionType.undo}
        userEdit={lastRedoUserEdit}
        open={isUndoTooltipOpen}
        onClose={handleUndoClose}
      />
      <UndoRedoActionTooltip
        anchorEl={redoButtonRef}
        type={ActionType.redo}
        userEdit={lastUndoUserEdit}
        open={isRedoTooltipOpen}
        onClose={handleRedoClose}
      />

      <FaroDialog
        title="Discard changes?"
        variant="danger"
        confirmText="Discard changes"
        cancelText="Resume editing"
        onConfirm={resetAllEdits}
        onClose={closeDialog}
        onCancel={closeDialog}
        isConfirmDisabled={false}
        open={isDialogOpen}
      >
        <FaroText variant="bodyM">
          Resetting will discard all your edits. To keep them, resume editing
          and use <b>Undo</b> to revert specific changes.
        </FaroText>
      </FaroDialog>
    </>
  );
}

type UndoRedoActionTooltipProps = Pick<PopperProps, "anchorEl" | "open"> & {
  type: ActionType;
  userEdit?: UserEdit;
  onClose(): void;
};

/**
 * @returns the tooltip message after the user performs an undo/redo action.
 */
function UndoRedoActionTooltip({
  type,
  userEdit,
  open,
  onClose,
  ...popperProps
}: UndoRedoActionTooltipProps): JSX.Element | null {
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  }, []);

  // Automatically close the tooltip after a delay
  useEffect(() => {
    clearCloseTimeout();

    if (open) {
      closeTimeout.current = setTimeout(() => {
        onClose();
      }, UNDO_REDO_POPUP_LIFE_TIME);
    }

    return () => {
      clearCloseTimeout();
    };
  }, [open, clearCloseTimeout, onClose, userEdit]);

  if (!userEdit) return null;

  return (
    // This will be changed once the FaroPopover ticket is done: https://faro01.atlassian.net/browse/NRT-2680
    <Popper
      open={open}
      sx={{
        // Popper should be placed on top of tooltips
        zIndex: 2000,
      }}
      {...popperProps}
      placement="left"
      unselectable="on"
    >
      <Box
        component="div"
        sx={{
          backgroundColor: grey[950],
          borderRadius: 1,
          paddingX: 1.25,
          paddingY: 1,
          marginRight: 1,
        }}
      >
        <IconText
          variant="bodyS"
          icon={<Checkmark2Icon sx={{ width: "20px", height: "20px" }} />}
          text={<TooltipMessage type={type} userEdit={userEdit} />}
          dark
        />
      </Box>
    </Popper>
  );
}

function TooltipMessage({
  type,
  userEdit,
}: {
  type: ActionType;
  userEdit: UserEdit;
}): JSX.Element {
  return (
    <>
      {type === ActionType.undo ? "Undid: " : "Redid: "}
      <EditDescription userEdit={userEdit} />
    </>
  );
}

/**
 * @param userEdit the user edit to generate the tooltip message for.
 * @returns Generates a descriptive tooltip message based on the type of user edit.
 */
function EditDescription({ userEdit }: { userEdit: UserEdit }): JSX.Element {
  switch (userEdit.type) {
    case UserEditType.addConnection:
      return <>Added a new connection between two captures</>;

    case UserEditType.deleteConnection:
      return <>Deleted a connection of two captures</>;

    case UserEditType.moveScans: {
      const action = userEdit.mode === "translation" ? "Moved" : "Rotated";
      return (
        <>
          {action}{" "}
          <TranslateVar name="scanCount">
            {userEdit.scanIds.length}
          </TranslateVar>{" "}
          capture(s)
        </>
      );
    }

    default:
      return <>Unknown action</>;
  }
}

/**
 *
 * Sends an analytics event when the user undoes an edit.
 *
 * @param userEdit The user edit that was undone, or undefined if there's no edit to undo.
 * @param inputSource The source of the input: "toolbar" or "keyboard".
 */
function sendUndoAnalyticsEvent(
  userEdit: UserEdit,
  inputSource: "toolbar" | "keyboard",
): void {
  if (isConnectionEdit(userEdit)) {
    Analytics.track(EventType.undoUserEdit, {
      action: "undo",
      editType: userEdit.type,
      inputSource,
      affectedScans: 2,
    });
  } else {
    Analytics.track(EventType.undoUserEdit, {
      action: "undo",
      editType: userEdit.type,
      inputSource,
      affectedScans: userEdit.scanIds.length,
    });
  }
}

/**
 * Sends an analytics event when the user redoes an edit.
 *
 * @param userEdit The user edit that was redone, or undefined if there's no edit to redo.
 * @param inputSource The source of the input: "toolbar" or "keyboard ".
 */
function sendRedoAnalyticsEvent(
  userEdit: UserEdit,
  inputSource: "toolbar" | "keyboard",
): void {
  if (isConnectionEdit(userEdit)) {
    Analytics.track(EventType.redoUserEdit, {
      action: "redo",
      editType: userEdit.type,
      inputSource,
      affectedScans: 2,
    });
  } else {
    Analytics.track(EventType.redoUserEdit, {
      action: "redo",
      editType: userEdit.type,
      inputSource,
      affectedScans: userEdit.scanIds.length,
    });
  }
}

/**
 * Sends an analytics event when the user resets all edits.
 */
function sendResetAnalyticsEvent(): void {
  Analytics.track(EventType.resetUserEdits, {
    action: "reset",
    editType: "ResetEdits",
    inputSource: "toolbar",
    affectedScans: 0,
  });
}
